/**
 * MSAL Configuration for CHEC Authentication
 *
 * Handles both IAM (internal users) and CIAM (external users) authentication.
 * The appropriate tenant is selected based on the current hostname.
 */

// Azure AD App Registration for CHEC (per-environment, set via REACT_APP_MSAL_CLIENT_ID build arg)
const CHEC_CLIENT_ID = process.env.REACT_APP_MSAL_CLIENT_ID

// Tenant IDs
const IAM_TENANT_ID = '505cca53-5750-4134-9501-8d52d5df3cd1' // Internal users
const CIAM_TENANT_ID = '88f558f5-a216-470b-b34a-3164f5d6ec0f' // External users

// Internal domains (IAM authentication)
const INTERNAL_DOMAINS = [
  'tbintra.net',
  'corpintra.net',
  'app.corpintra.net',
  'cloud.tbintra.net',
]

/**
 * Determine if the current host is an internal domain
 */
export const isInternalDomain = () => {
  const { hostname } = window.location

  // localhost is treated as internal for development
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    // Check env variable to override for testing external auth
    return process.env.REACT_APP_AUTH_MODE !== 'external'
  }

  return INTERNAL_DOMAINS.some((domain) => hostname.endsWith(domain))
}

/**
 * Get the appropriate tenant ID based on the current domain
 */
export const getTenantId = () =>
  isInternalDomain() ? IAM_TENANT_ID : CIAM_TENANT_ID

/**
 * Get the MSAL configuration for the current environment
 */
export const getMsalConfig = () => {
  const tenantId = getTenantId()
  const redirectUri = `${window.location.origin}/chec/sso`

  return {
    auth: {
      clientId: CHEC_CLIENT_ID,
      authority: `https://login.microsoftonline.com/${tenantId}`,
      redirectUri,
      postLogoutRedirectUri: redirectUri,
      navigateToLoginRequestUrl: true,
    },
    cache: {
      cacheLocation: 'sessionStorage',
      storeAuthStateInCookie: false,
    },
    system: {
      loggerOptions: {
        logLevel:
          process.env.NODE_ENV === 'development'
            ? 'Info' // LogLevel.Info
            : 'Error', // LogLevel.Error
        piiLoggingEnabled: false,
      },
    },
  }
}

/**
 * Scopes to request during authentication
 * Using default scopes - the API validates the tid claim, not specific scopes
 */
export const loginRequest = {
  scopes: ['openid', 'profile', 'email'],
}

/**
 * Scopes for API calls (silent token acquisition)
 */
export const apiTokenRequest = {
  scopes: [`api://${CHEC_CLIENT_ID}/.default`],
}

export { CHEC_CLIENT_ID, IAM_TENANT_ID, CIAM_TENANT_ID }
